import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';


const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const { user, userProfile, signOut, isAuthenticated } = useAuth();
  
  const userRole = userProfile?.role || 'jobseeker';
  const userName = userProfile?.fullName || 'User';
  const userEmail = user?.email || '';

  const navigationItems = [
    {
      label: 'Dashboard',
      path: userRole === 'employer' ? '/employer-dashboard' : '/job-seeker-dashboard',
      icon: 'LayoutDashboard',
      roles: ['jobseeker', 'employer'],
    },
    {
      label: 'Jobs',
      path: '/job-listings',
      icon: 'Briefcase',
      roles: ['jobseeker'],
    },
    {
      label: 'Applications',
      path: '/application-tracking',
      icon: 'FileText',
      roles: ['jobseeker'],
    },
    {
      label: 'Candidates',
      path: '/candidate-management',
      icon: 'Users',
      roles: ['employer'],
    },
    {
      label: 'Post Job',
      path: '/post-new-job',
      icon: 'PlusCircle',
      roles: ['employer'],
    },
  ];

  const moreMenuItems = [
    {
      label: 'Company',
      path: '/company-profile',
      icon: 'Building2',
      roles: ['employer'],
    },
    {
      label: 'Profile',
      path: '/user-profile-settings',
      icon: 'User',
      roles: ['jobseeker', 'employer'],
    },
  ];

  const filteredNavItems = navigationItems?.filter(item => 
    item?.roles?.includes(userRole)
  );

  const filteredMoreItems = moreMenuItems?.filter(item => 
    item?.roles?.includes(userRole)
  );

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/user-login');
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isUserMenuOpen) setIsUserMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[1000] bg-card border-b border-border">
        <div className="flex items-center justify-between h-16 px-4 lg:px-6">
          <div className="flex items-center gap-6">
            <Link to={userRole === 'employer' ? '/employer-dashboard' : '/job-seeker-dashboard'} className="flex items-center gap-2">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg transition-smooth hover:bg-primary/20">
                <Icon name="Briefcase" size={24} color="var(--color-primary)" />
              </div>
              <span className="text-xl font-semibold text-foreground hidden sm:block">JobPortal India</span>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {filteredNavItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                </Link>
              ))}

              {filteredMoreItems?.length > 0 && (
                <div className="relative">
                  <button
                    onClick={toggleUserMenu}
                    className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-foreground hover:bg-muted transition-smooth"
                  >
                    <Icon name="MoreHorizontal" size={18} />
                    <span>More</span>
                  </button>

                  {isUserMenuOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-[1100]" 
                        onClick={() => setIsUserMenuOpen(false)}
                      />
                      <div className="absolute top-full right-0 mt-2 w-56 bg-popover border border-border rounded-lg shadow-modal z-[1200]">
                        <div className="py-2">
                          {filteredMoreItems?.map((item) => (
                            <Link
                              key={item?.path}
                              to={item?.path}
                              onClick={() => setIsUserMenuOpen(false)}
                              className={`flex items-center gap-3 px-4 py-2 text-sm transition-smooth ${
                                isActivePath(item?.path)
                                  ? 'bg-muted text-primary font-medium' :'text-popover-foreground hover:bg-muted'
                              }`}
                            >
                              <Icon name={item?.icon} size={18} />
                              <span>{item?.label}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden lg:block relative">
              <button
                onClick={toggleUserMenu}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-smooth"
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="User" size={18} color="var(--color-primary)" />
                </div>
                <div className="text-left hidden xl:block">
                  <div className="text-sm font-medium text-foreground">{userName}</div>
                  <div className="text-xs text-muted-foreground capitalize">{userRole}</div>
                </div>
                <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
              </button>

              {isUserMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-[1100]" 
                    onClick={() => setIsUserMenuOpen(false)}
                  />
                  <div className="absolute top-full right-0 mt-2 w-64 bg-popover border border-border rounded-lg shadow-modal z-[1200]">
                    <div className="p-4 border-b border-border">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon name="User" size={20} color="var(--color-primary)" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-popover-foreground truncate">{userName}</div>
                          <div className="text-xs text-muted-foreground truncate">{userEmail}</div>
                        </div>
                      </div>
                    </div>
                    <div className="py-2">
                      <Link
                        to="/user-profile-settings"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                      >
                        <Icon name="Settings" size={18} />
                        <span>Settings</span>
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-error hover:bg-muted transition-smooth"
                      >
                        <Icon name="LogOut" size={18} />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-lg hover:bg-muted transition-smooth"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>
      {isMobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1100] lg:hidden"
            onClick={closeMobileMenu}
          />
          <div className="fixed top-16 left-0 right-0 bottom-0 bg-card border-t border-border z-[1100] lg:hidden overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center gap-3 p-4 mb-4 bg-muted rounded-lg">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="User" size={24} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{userName}</div>
                  <div className="text-xs text-muted-foreground truncate">{userEmail}</div>
                  <div className="text-xs text-muted-foreground capitalize mt-1">{userRole}</div>
                </div>
              </div>

              <nav className="space-y-1">
                {filteredNavItems?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-smooth ${
                      isActivePath(item?.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span>{item?.label}</span>
                  </Link>
                ))}

                {filteredMoreItems?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-smooth ${
                      isActivePath(item?.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span>{item?.label}</span>
                  </Link>
                ))}
              </nav>

              <div className="mt-6 pt-6 border-t border-border">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-error hover:bg-muted transition-smooth"
                >
                  <Icon name="LogOut" size={20} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;